import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, PhoneCall } from "lucide-react";
import { useTestPlaceAiCallMutation } from "@/store/admin_api";
import { AiAgent } from "@/model/interface/AiAgent";
import { apiErrorCode, apiErrorMessage } from "@/util/apiError";

type TestCallResult =
  | { kind: "placed"; detail: string }
  | { kind: "blocked"; detail: string };

export default function WizardTestStep({ agent }: { agent?: AiAgent }) {
  const [placeTestCall, { isLoading: isPlacingCall }] = useTestPlaceAiCallMutation();
  const [toPhoneNumber, setToPhoneNumber] = useState("");
  const [callScope, setCallScope] = useState("");
  const [result, setResult] = useState<TestCallResult | null>(null);

  const organisationId = agent?.organisation?.id;

  const handlePlaceTestCall = async () => {
    if (!agent || !organisationId || !toPhoneNumber.trim()) {
      return;
    }
    setResult(null);
    try {
      const placedCall = await placeTestCall({
        organisationId,
        agentId: agent.id,
        toPhoneNumber: toPhoneNumber.trim(),
        callScope: callScope.trim() || null,
      }).unwrap();
      setResult({
        kind: "placed",
        detail: `Call placed. Call id ${placedCall.id}, carrier sid ${placedCall.carrierCallSid ?? "pending"}.`,
      });
    } catch (error: unknown) {
      if (apiErrorCode(error) === "NO_CONSENT") {
        setResult({
          kind: "blocked",
          detail:
            "This number has no recorded call consent in that organisation, so the call was blocked. Record consent from the organisation's own agent page, then try again. The agent is already deployed either way.",
        });
        return;
      }
      setResult({ kind: "blocked", detail: apiErrorMessage(error, "Failed to place call") });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Place a test call</CardTitle>
        <CardDescription>
          {agent
            ? `Calls out as ${agent.name} for ${agent.organisation?.name ?? "the organisation"}. Optional, the agent is already live.`
            : "Optional, the agent is already live."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Destination number (E.164) *</Label>
            <Input
              type="tel"
              value={toPhoneNumber}
              onChange={(event) => setToPhoneNumber(event.target.value)}
              placeholder="+15551234567"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Call scope <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea
              rows={2}
              value={callScope}
              onChange={(event) => setCallScope(event.target.value)}
              placeholder="What this call is about, passed to the agent as context"
            />
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="gap-1.5"
          onClick={handlePlaceTestCall}
          disabled={!agent || !organisationId || !toPhoneNumber.trim() || isPlacingCall}
        >
          {isPlacingCall ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
          Place test call
        </Button>

        {result && (
          <p
            className={
              result.kind === "placed"
                ? "rounded-md border bg-success-soft px-3 py-2 text-sm text-success-soft-foreground"
                : "rounded-md border bg-warning-soft px-3 py-2 text-sm text-warning-soft-foreground"
            }
          >
            {result.detail}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
